import React, { useState } from 'react';
import { useAdminApi } from '../../hooks/useAdminApi';
import {
  X,
  RotateCcw,
  AlertCircle,
  Loader2,
  CheckCircle,
  Calendar,
  Key,
  Building2
} from 'lucide-react';

const RenewLicenseModal = ({ license, onClose, onSave }) => {
  const { put } = useAdminApi();
  const [formData, setFormData] = useState({
    duration: 12, // months
    upgradePlan: false,
    newPlan: license?.plan || 'starter'
  });
  const [renewing, setRenewing] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState(false);

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : (name === 'duration' ? parseInt(value) : value)
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setRenewing(true);

    try {
      const payload = {
        duration: formData.duration
      };

      if (formData.upgradePlan) {
        payload.plan = formData.newPlan;
      }

      const response = await put(`/licenses/${license._id}/renew`, payload);

      if (response.success) {
        setSuccess(true);
      } else {
        setError(response.error || 'Failed to renew license');
      }
    } catch (err) {
      setError(err.message || 'An error occurred');
    } finally {
      setRenewing(false);
    }
  };

  const formatDate = (date) => {
    return new Date(date).toLocaleDateString('en-IN', {
      day: 'numeric',
      month: 'long',
      year: 'numeric'
    });
  };

  const calculateNewExpiry = () => {
    const currentExpiry = new Date(license?.expiresAt);
    const now = new Date();
    const startDate = currentExpiry > now ? currentExpiry : now;
    const newExpiry = new Date(startDate);
    newExpiry.setMonth(newExpiry.getMonth() + formData.duration);
    return newExpiry;
  };

  const handleClose = () => {
    if (success) {
      onSave();
    } else {
      onClose();
    }
  };

  return (
    <div className="fixed inset-0 z-50 overflow-y-auto">
      <div className="flex items-center justify-center min-h-screen px-4 pt-4 pb-20 text-center sm:p-0">
        {/* Backdrop */}
        <div
          className="fixed inset-0 bg-black/50 transition-opacity"
          onClick={handleClose}
        />

        {/* Modal */}
        <div className="relative bg-white rounded-2xl shadow-xl max-w-lg w-full mx-auto z-10">
          {/* Header */}
          <div className="flex items-center justify-between px-6 py-4 border-b border-gray-100">
            <div className="flex items-center">
              <div className="w-10 h-10 bg-gradient-to-r from-green-500 to-emerald-500 rounded-xl flex items-center justify-center mr-3">
                <RotateCcw className="w-5 h-5 text-white" />
              </div>
              <div>
                <h2 className="text-lg font-semibold text-gray-900">Renew License</h2>
                <p className="text-sm text-gray-500">{license?.tenant?.companyName}</p>
              </div>
            </div>
            <button
              onClick={handleClose}
              className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
            >
              <X className="w-5 h-5 text-gray-500" />
            </button>
          </div>

          {/* Content */}
          <div className="p-6">
            {error && (
              <div className="mb-4 p-4 bg-red-50 border border-red-200 rounded-xl flex items-start">
                <AlertCircle className="w-5 h-5 text-red-500 mt-0.5 mr-3 flex-shrink-0" />
                <p className="text-sm text-red-700">{error}</p>
              </div>
            )}

            {success ? (
              // Success View
              <div className="text-center">
                <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
                  <CheckCircle className="w-8 h-8 text-green-600" />
                </div>
                <h3 className="text-lg font-semibold text-gray-900 mb-2">License Renewed!</h3>
                <p className="text-gray-500 mb-6">
                  The license has been successfully renewed for {formData.duration} months.
                </p>

                <div className="bg-green-50 rounded-xl p-4 mb-6">
                  <div className="grid grid-cols-2 gap-4 text-left">
                    <div>
                      <p className="text-sm text-green-700">New Expiry Date</p>
                      <p className="font-semibold text-green-800">
                        {formatDate(calculateNewExpiry())}
                      </p>
                    </div>
                    <div>
                      <p className="text-sm text-green-700">Plan</p>
                      <p className="font-semibold text-green-800 capitalize">
                        {formData.upgradePlan ? formData.newPlan : license?.plan}
                      </p>
                    </div>
                  </div>
                </div>

                <button
                  onClick={handleClose}
                  className="w-full px-6 py-3 bg-gradient-to-r from-green-600 to-emerald-600 text-white rounded-xl hover:from-green-700 hover:to-emerald-700 transition-colors"
                >
                  Done
                </button>
              </div>
            ) : (
              // Renewal Form
              <form onSubmit={handleSubmit}>
                {/* Current License Info */}
                <div className="bg-gray-50 rounded-xl p-4 mb-6">
                  <h4 className="text-sm font-semibold text-gray-700 mb-3">Current License</h4>
                  <div className="grid grid-cols-2 gap-4 text-sm">
                    <div className="flex items-center">
                      <Key className="w-4 h-4 text-gray-400 mr-2" />
                      <div>
                        <p className="text-gray-500">License Key</p>
                        <p className="font-mono text-gray-900">{license?.licenseKey?.substring(0, 15)}...</p>
                      </div>
                    </div>
                    <div className="flex items-center">
                      <Building2 className="w-4 h-4 text-gray-400 mr-2" />
                      <div>
                        <p className="text-gray-500">Current Plan</p>
                        <p className="font-medium text-gray-900 capitalize">{license?.plan}</p>
                      </div>
                    </div>
                    <div className="flex items-center col-span-2">
                      <Calendar className="w-4 h-4 text-gray-400 mr-2" />
                      <div>
                        <p className="text-gray-500">Current Expiry</p>
                        <p className="font-medium text-gray-900">{formatDate(license?.expiresAt)}</p>
                      </div>
                    </div>
                  </div>
                </div>

                {/* Duration Selection */}
                <div className="mb-6">
                  <label className="block text-sm font-medium text-gray-700 mb-3">
                    Renewal Duration
                  </label>
                  <div className="grid grid-cols-3 gap-3">
                    {[
                      { value: 1, label: '1 Month' },
                      { value: 3, label: '3 Months' },
                      { value: 6, label: '6 Months' },
                      { value: 12, label: '12 Months' },
                      { value: 24, label: '24 Months' },
                      { value: 36, label: '36 Months' }
                    ].map((option) => (
                      <button
                        key={option.value}
                        type="button"
                        onClick={() => setFormData(prev => ({ ...prev, duration: option.value }))}
                        className={`p-3 border-2 rounded-xl text-center transition-all ${
                          formData.duration === option.value
                            ? 'border-green-500 bg-green-50 text-green-700'
                            : 'border-gray-200 hover:border-gray-300 text-gray-700'
                        }`}
                      >
                        <span className="font-medium text-sm">{option.label}</span>
                      </button>
                    ))}
                  </div>
                </div>

                {/* New Expiry Preview */}
                <div className="bg-blue-50 border border-blue-200 rounded-xl p-4 mb-6">
                  <div className="flex items-center">
                    <Calendar className="w-5 h-5 text-blue-600 mr-3" />
                    <div>
                      <p className="text-sm text-blue-700">New Expiry Date</p>
                      <p className="font-semibold text-blue-800">
                        {formatDate(calculateNewExpiry())}
                      </p>
                    </div>
                  </div>
                </div>

                {/* Plan Upgrade Option */}
                <div className="mb-6">
                  <label className="flex items-center cursor-pointer">
                    <input
                      type="checkbox"
                      name="upgradePlan"
                      checked={formData.upgradePlan}
                      onChange={handleChange}
                      className="sr-only"
                    />
                    <span className={`w-5 h-5 border-2 rounded flex items-center justify-center mr-3 ${
                      formData.upgradePlan ? 'border-green-500 bg-green-500' : 'border-gray-300'
                    }`}>
                      {formData.upgradePlan && <CheckCircle className="w-3 h-3 text-white" />}
                    </span>
                    <span className="font-medium text-gray-700">Upgrade plan during renewal</span>
                  </label>

                  {formData.upgradePlan && (
                    <div className="mt-4 pl-8">
                      <select
                        name="newPlan"
                        value={formData.newPlan}
                        onChange={handleChange}
                        className="w-full px-4 py-2.5 border border-gray-200 rounded-xl focus:ring-2 focus:ring-green-500 focus:border-transparent"
                      >
                        <option value="starter">Starter</option>
                        <option value="professional">Professional</option>
                        <option value="enterprise">Enterprise</option>
                      </select>
                    </div>
                  )}
                </div>

                {/* Submit Button */}
                <button
                  type="submit"
                  disabled={renewing}
                  className="w-full flex items-center justify-center px-6 py-3 bg-gradient-to-r from-green-600 to-emerald-600 text-white rounded-xl hover:from-green-700 hover:to-emerald-700 transition-colors disabled:opacity-50"
                >
                  {renewing ? (
                    <>
                      <Loader2 className="w-5 h-5 mr-2 animate-spin" />
                      Renewing...
                    </>
                  ) : (
                    <>
                      <RotateCcw className="w-5 h-5 mr-2" />
                      Renew License
                    </>
                  )}
                </button>
              </form>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default RenewLicenseModal;
